import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { setStoredLocale } from './config'
import { type Direction, directionFor, isLocale, type Locale } from './locales'

/**
 * Keeps `<html lang>` and `<html dir>` in step with the active locale.
 *
 * Both attributes have to be on `<html>`, not on a wrapper div:
 *
 *  - `dir` is what makes every logical CSS property (`ms-*`, `text-start`,
 *    `border-e`) resolve to the correct physical side. Set it lower in the tree
 *    and anything portalled to `document.body` — dialogs, dropdowns, toasts —
 *    escapes it and renders LTR inside an RTL page.
 *  - `lang` drives font selection, hyphenation and screen-reader pronunciation.
 *
 * Mount once, at the application root.
 */
export function useDocumentDirection(): Direction {
  const { i18n } = useTranslation()
  const locale = i18n.language
  const direction = directionFor(locale)

  useEffect(() => {
    const root = document.documentElement
    root.setAttribute('lang', locale)
    root.setAttribute('dir', direction)
  }, [locale, direction])

  return direction
}

export function useLocale(): {
  locale: Locale
  direction: Direction
  setLocale: (next: Locale) => void
} {
  const { i18n } = useTranslation()
  const locale = isLocale(i18n.language) ? i18n.language : 'en'

  const setLocale = useCallback(
    (next: Locale) => {
      setStoredLocale(next)
      void i18n.changeLanguage(next)
    },
    [i18n],
  )

  return { locale, direction: directionFor(locale), setLocale }
}
